import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { SaveSearchFilterComponent } from "@components/save-search-filter/save-search-filter.component";
import { SavedSearchFilterTabsComponent } from "@components/saved-search-filter-tabs/saved-search-filter-tabs.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import _ from "lodash";
import { Observable, Subject, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { INamedSearchFilter } from "~app/common/components/save-search-filter/model/INamedSearchFilter";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Constants, IExternVirkeskopLeverantor, IExternVirkeskopSearchModel, IExternVirkeskopStatus } from "../../models/externvirkeskop-models";
import { ExternVirkeskopService } from "../../services/extern-virkeskop.service";

@Component({
    selector: "rask-extern-virkeskop-search-panel",
    templateUrl: "./extern-virkeskop-search-panel.component.html",
    styleUrls: ["./extern-virkeskop-search-panel.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SavedSearchFilterTabsComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiSelectorComponent,
        InputComponent,
        DatepickerComponent,
        SaveSearchFilterComponent,
        SubmitButtonComponent,
        ButtonComponent
    ]
})
export class ExternVirkeskopSearchPanelComponent implements OnInit, OnDestroy {
    readonly webbInstallningarRecent = "externkontrakt.searchparams";
    readonly webbInstallningarSparadeKey = "search.externkontrakt.saved.filters";

    protected form: UntypedFormGroup;
    protected leverantorList: Observable<IExternVirkeskopLeverantor[]>;
    protected model: IExternVirkeskopSearchModel;
    protected statusList: Observable<IExternVirkeskopStatus[]>;

    private readonly destroy$ = new Subject<void>();
    private readonly externVirkeskopService = inject(ExternVirkeskopService);
    private readonly formBinderService = inject(FormBinderService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    leverantorSearchFn = (val: string, item: IExternVirkeskopLeverantor) => item.namn?.toUpperCase().includes(val?.toUpperCase()) ?? false;

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.externVirkeskopService.getAllExternaOrganisationer().subscribe(leverantorer => {
            this.leverantorList = of(leverantorer.map(l => ({ namn: l })));
            this.statusList = of(Constants.statuses.map(x => ({ namn: x,
                id: 0 })));
            this.initSearchModel();
            this.form = this.formBinderService.CreateGroup(this.model);
            this.search();
            this.externVirkeskopService.documentsReImported$.pipe(
                takeUntil(this.destroy$)
            ).subscribe(x => this.search());
        });
    }

    statusSearchFn = (val: string, item: string) => item.toUpperCase().includes(val?.toUpperCase()) ?? false;

    protected onFilterChanged(filter: INamedSearchFilter): void {
        this.model = _.cloneDeep(filter.filter);
        this.formBinderService.reBindForm(this.form, this.model);
    }

    protected onSearch(): void {
        this.webinstallningarService.set<IExternVirkeskopSearchModel>(this.webbInstallningarRecent, ObjectUtils.removeUndefinedProperties(this.model, true));
        this.search();
    }

    protected rensaSokFilter(): void {
        this.initSearchModel(false);
        this.formBinderService.reBindForm(this.form, _.cloneDeep(this.model));
        this.search();
    }

    private initSearchModel(restoreWebinstallning = true): void {
        this.model = {
            externBeteckning: "",
            leverantorer: [],
            statusar: [],
            datumMottagen: "",
            datumUppdaterad: ""
        };
        if (restoreWebinstallning) {
            this.webinstallningarService.applyTo(this.webbInstallningarRecent, ObjectUtils.removeUndefinedProperties(this.model, true));
        }
    }

    private search(): void {
        const searchModel = _.cloneDeep(this.model);
        this.externVirkeskopService.searchForExternVirkeskopskontrakt(searchModel);
    }
}
